import {
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMyWatchedLotIds,
  houseLabel,
  type BidderHouse,
  feedSaleStateFromAuctionStatus,
  withLiveFigures,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Countdown } from '../../components/Countdown';
import { EmptyState, LotCard, SectionHead, type LotCardLot } from '../../components/bidder-web';
import { SaleAside } from '../../components/bidder-web/SaleAside';
import { useLiveSaleAside } from '../../components/bidder-web/useLiveSaleAside';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useConnection } from '../../hooks/useConnection';
import { useLiveClock } from '../../hooks/useLiveClock';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useSaleFeed } from '../../hooks/useSaleFeed';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type Auction = Pick<
  Tables<'auctions'>,
  | 'id'
  | 'slug'
  | 'title'
  | 'description'
  | 'preview_text'
  | 'status'
  | 'ends_at'
  | 'lot_count'
  | 'tenant_id'
  | 'buyers_premium_bps'
  | 'extension_threshold_seconds'
  | 'extension_amount_seconds'
  | 'requires_cc_preauth'
>;

const AUCTION_COLUMNS =
  'id, slug, title, description, preview_text, status, ends_at, lot_count, tenant_id, buyers_premium_bps, extension_threshold_seconds, extension_amount_seconds, requires_cc_preauth';

type HomeLot = LotCardLot & { category_id: string | null; status: string };

const LOT_COLUMNS =
  'id, lot_number, title, current_bid_cents, starting_bid_cents, bid_count, ends_at, has_reserve, category_id, status';

/** How many lots the "closing soonest" strip carries. Two rows of four. */
const CLOSING_SOONEST = 8;

/**
 * Home — the page a bidder arriving from a search result actually lands on.
 *
 * REBUILT to the web design handoff (`web/web-pages.jsx`, 2026-09-02). It used
 * to be a router: a signed-in bidder was bounced straight to `/auctions` and a
 * signed-out visitor was bounced to sign-in, so the house's own front page did
 * not exist. On a white-label site that is the page with the most to do.
 *
 * 🔴 IT IS PUBLIC NOW. Gating `/` behind sign-in meant a visitor following a
 * link to the house's site was met with a login form before being told whose
 * site it was or what was in the sale. The routing job the old page did is
 * still here, but only for the case that actually needs it: a bidder registered
 * with several houses and no current pick.
 *
 * THE THREE HOW-IT-WORKS FACTS ARE THE APP'S FIRST-RUN CONTENT. There is no
 * install on the web, no permission ask and no fresh-app moment, so porting
 * first run literally would put an interstitial between a bidder and the lot
 * they clicked from Google. The same three facts live on the page instead.
 */
export function BidderHome() {
  const {
    houses,
    loading: housesLoading,
    error: housesError,
    selectedId,
    selectHouse,
    selected,
    active,
    discoverHouse,
  } = useBidderHouses();
  const { session, userId } = useSession();
  const { state: connection } = useConnection();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [lots, setLots] = useState<HomeLot[]>([]);
  const [photos, setPhotos] = useState<ReadonlyMap<string, { url: string }[]>>(new Map());
  const [categories, setCategories] = useState<{ id: string; name: string; count: number }[]>([]);
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());

  /**
   * The standing and the activity feed, for the panels beside the sale.
   *
   * 🔴 HOME'S OWN COUNTS WERE A THIRD IMPLEMENTATION, AND SILENTLY UNSCOPED.
   * Until 2026-09-18 this page counted the bidder's open lots from a
   * `bids -> lots` join with NO auction filter, and `watching` from the whole
   * watchlist — so the panel sitting beside ONE sale described every sale the
   * bidder was in, while the identical panel on the lot page described one.
   * Three pages, three scopes, one component.
   *
   * It is scoped to the sale it sits next to, which is what a panel drawn
   * beside a sale has to mean. The watchlist is the page that legitimately
   * spans sales, and it asks for `'everywhere'`.
   *
   * LIVE since 2026-10-11, by the Live Room's rule (`useLiveSaleAside`): the
   * featured sale's feed brings its ticker for Activity, and its standing
   * counts are asked again after the bidder's own standing moves. The same
   * feed keeps the closing-soonest cards' prices, counts and clocks live —
   * the shared `withLiveFigures`, as on every other page that lists lots.
   */
  const { source } = useLiveSource(lots[0]?.id ?? null);
  const live = useSaleFeed(source, {
    view: lots.map((l) => l.id),
    ticker: true,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const nowServerMs = useLiveClock(live.feed);
  const { standing, events: heroEvents } = useLiveSaleAside({
    auctionId: auction?.id ?? null,
    feed: live.feed,
    sourceKind: source?.kind ?? null,
    feedStatus: live.status,
    offline: connection === 'offline',
  });
  const liveFeed = live.feed;
  const shownLots = useMemo(
    () => lots.map((l) => withLiveFigures(l, liveFeed?.lot(l.id))),
    // `live.version` re-reads the feed.
    [lots, liveFeed, live.version],
  );
  const [loading, setLoading] = useState(true);

  const needsHousePick = houses.length > 1 && selectedId === null;

  useEffect(() => {
    // A bidder still choosing a house has no sale to feature — loading one for
    // the house they have not picked would be the wrong catalogue.
    if (needsHousePick) return;
    let cancelled = false;
    setLoading(true);

    void (async () => {
      let query = supabase
        .from('auctions')
        .select(AUCTION_COLUMNS)
        .eq('is_visible', true)
        .in('status', ['active', 'preview'])
        .order('ends_at', { ascending: true, nullsFirst: false })
        .limit(1);
      if (selected) query = query.eq('tenant_id', selected.tenant_id);

      const { data } = await query;
      if (cancelled) return;
      const sale = (data ?? [])[0] ?? null;
      setAuction(sale);

      if (sale === null) {
        setLots([]);
        setLoading(false);
        return;
      }

      void fetchHouseIdentity(supabase, sale.tenant_id).then((house) => {
        if (!cancelled) discoverHouse(house);
      });

      // Two reads rather than one: the strip needs eight FULL rows, and the
      // category counts need every lot's category and nothing else. Fetching
      // whole rows for a 200-lot sale to count six categories would be the
      // expensive way to answer a cheap question.
      const [{ data: soonRows }, { data: catRows }] = await Promise.all([
        supabase
          .from('lots')
          .select(LOT_COLUMNS)
          .eq('auction_id', sale.id)
          .eq('is_visible', true)
          .eq('status', 'active')
          .order('ends_at', { ascending: true, nullsFirst: false })
          .limit(CLOSING_SOONEST),
        supabase
          .from('lots')
          .select('category_id')
          .eq('auction_id', sale.id)
          .eq('is_visible', true)
          .eq('status', 'active'),
      ]);
      if (cancelled) return;

      const soon = (soonRows ?? []) as HomeLot[];
      setLots(soon);
      setPhotos(await fetchLotPhotos(supabase, soon));
      if (cancelled) return;

      const counts = new Map<string, number>();
      for (const row of (catRows ?? []) as { category_id: string | null }[]) {
        if (row.category_id === null) continue;
        counts.set(row.category_id, (counts.get(row.category_id) ?? 0) + 1);
      }
      if (counts.size > 0) {
        const { data: catNames } = await supabase
          .from('item_categories')
          .select('id, name')
          .in('id', [...counts.keys()])
          .order('sort_order', { ascending: true, nullsFirst: false });
        if (cancelled) return;
        setCategories(
          ((catNames ?? []) as { id: string; name: string }[])
            .map((c) => ({ ...c, count: counts.get(c.id) ?? 0 }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6),
        );
      } else {
        setCategories([]);
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selected, needsHousePick, discoverHouse]);

  /**
   * Which lots this bidder is watching — for the HEART on each card, which is a
   * per-lot question and stays a client-side set.
   *
   * ⚠️ IT NO LONGER FEEDS THE COUNTS. The `bids -> lots` join that used to sit
   * here computed the standing in TypeScript, unscoped, and is gone: see
   * `useLiveSaleAside` above. A failure here costs a heart outline, not a page.
   */
  useEffect(() => {
    if (userId === null) {
      setWatchedIds(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      const watched = await fetchMyWatchedLotIds(supabase, userId).catch((): string[] => []);
      if (cancelled) return;
      setWatchedIds(new Set(watched));
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const closingTonight = useMemo(() => {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return lots.filter((l) => l.ends_at !== null && Date.parse(l.ends_at) <= endOfDay.getTime())
      .length;
  }, [lots]);

  if (needsHousePick) {
    return <HousePicker houses={houses} onSelect={selectHouse} />;
  }
  if (housesLoading && session) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading your auction houses…</p>
      </div>
    );
  }

  const houseName = active === null ? 'AuctiSync' : houseLabel(active);
  const guest = session === null;

  const premiumPct = auction?.buyers_premium_bps == null ? null : auction.buyers_premium_bps / 100;
  const extendMinutes =
    auction?.extension_threshold_seconds == null
      ? null
      : Math.round(auction.extension_threshold_seconds / 60);

  return (
    <>
      <section className="w-hero">
        <div className="w-hero-in">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              {auction === null ? null : connection === 'offline' ? (
                <span className="w-pill">Not updating</span>
              ) : (
                <span className="w-pill">
                  <span className="w-dot" />
                  {auction.status === 'active' ? 'Live now' : 'Preview'}
                </span>
              )}
              {auction?.ends_at ? (
                <span className="w-lbl">
                  Closes <Countdown endsAt={auction.ends_at} nowServerMs={nowServerMs} />
                </span>
              ) : null}
            </div>
            <h1 className="w-h1">{auction?.title ?? houseName}</h1>
            <p
              className="w-body"
              style={{ marginTop: 14, maxWidth: 520, color: 'var(--text-label-on-deep)' }}
            >
              {auction === null
                ? `${houseName} has no sale open at the moment. Past sales and their results are on the sales page.`
                : (auction.preview_text ??
                  auction.description ??
                  'Lots close in staggered order, and any bid in the closing window pushes that lot’s clock out.')}
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
              {auction === null ? (
                <Link to="/auctions" className="w-btn w-btn-cta w-btn-lg">
                  See past sales
                </Link>
              ) : (
                <Link to={`/browse?sale=${auction.slug}`} className="w-btn w-btn-cta w-btn-lg">
                  Browse {auction.lot_count} lots<span aria-hidden>›</span>
                </Link>
              )}
              {guest ? (
                <Link to="/register" className="w-btn w-btn-ghost w-btn-lg">
                  Register to bid
                </Link>
              ) : null}
            </div>
          </div>

          {/* 🔴 SIGNED IN GETS BOTH PANELS; A GUEST KEEPS THE SALE PITCH. Jim,
              2026-09-18: *"I want both of those tables in the headers on Home,
              Lot, and Watchlist."* The standing and the activity are what a
              bidder wants, and `SaleAside` is the same component the Lots and
              lot pages use — one definition, so the three cannot drift.

              ⚠️ THE GUEST CARD IS NOT REPLACED, DELIBERATELY. For a signed-out
              visitor this card is the sale pitch — lots open, closing today,
              buyer's premium — and "Sign in to see where you stand" beside an
              empty feed would be a worse page for the person we are trying to
              convert. They still get the activity feed below it. */}
          {!guest ? (
            <SaleAside
              standing={standing}
              events={heroEvents}
              offline={false}
              nowServerMs={nowServerMs}
            />
          ) : (
            <div className="w-hero-card">
              <div className="w-lbl" style={{ marginBottom: 12 }}>
                {guest ? 'This sale' : 'Where you stand'}
              </div>
              {guest ? (
                <>
                  {/*
                  REAL NUMBERS ONLY. The design's card also shows "bidders
                  registered"; that is not public data, and a plausible-looking
                  invented figure on the page that sells the sale is exactly the
                  kind of thing nobody ever goes back and fixes.
                */}
                  <HeroRow
                    label="Lots open"
                    value={auction === null ? '—' : String(auction.lot_count)}
                  />
                  <HeroRow
                    label="Closing today"
                    value={auction === null ? '—' : String(closingTonight)}
                  />
                  {premiumPct !== null ? (
                    <HeroRow label="Buyer’s premium" value={`${String(premiumPct)}%`} />
                  ) : null}
                  <p
                    className="w-body"
                    style={{ fontSize: 13, marginTop: 14, color: 'var(--text-label-on-deep)' }}
                  >
                    Bidding needs an account with {houseName} — they approve their own bidders.
                  </p>
                </>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <div className="w-wrap">
        {housesError ? (
          <p className="muted w-sec" style={{ fontSize: 14 }}>
            Your auction houses could not be loaded ({housesError}). Everything below still works.
          </p>
        ) : null}

        <section className="w-sec">
          <SectionHead
            label="Closing soonest"
            title="Ending first in this sale"
            {...(auction === null ? {} : { actionLabel: 'All lots', actionTo: '/browse' })}
          />
          {loading ? (
            <p className="muted">Loading lots…</p>
          ) : lots.length === 0 ? (
            <EmptyState
              icon="⏱"
              title="No lots are open right now"
              body="When a sale opens, the lots closing first appear here."
              action={
                <Link to="/auctions" className="w-btn w-btn-out">
                  See all auctions
                </Link>
              }
            />
          ) : (
            <div className="w-grid">
              {shownLots.map((lot) => (
                <LotCard
                  key={lot.id}
                  lot={lot}
                  auctionSlug={auction?.slug ?? ''}
                  photoUrl={photos.get(lot.id)?.[0]?.url ?? null}
                  nowServerMs={nowServerMs}
                  offline={connection === 'offline'}
                  {...(session ? { watched: watchedIds.has(lot.id) } : {})}
                />
              ))}
            </div>
          )}
        </section>

        {categories.length > 0 ? (
          <section className="w-sec">
            <SectionHead label="Browse" title="By category" />
            <div className="w-grid w-grid-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/browse?category=${encodeURIComponent(c.id)}`}
                  className="w-card"
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16 }}
                >
                  <span
                    className="w-empty-i"
                    style={{ width: 44, height: 44, borderRadius: 11, fontSize: 18 }}
                    aria-hidden
                  >
                    ⚒
                  </span>
                  <span style={{ textAlign: 'left' }}>
                    <span className="w-h3" style={{ display: 'block' }}>
                      {c.name}
                    </span>
                    <span className="muted" style={{ fontSize: 13 }}>
                      {c.count} lot{c.count === 1 ? '' : 's'}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <section className="w-sec">
          <SectionHead
            label="Before you bid"
            title="How bidding here works"
            actionLabel="Help centre"
            actionTo="/help"
          />
          <div className="w-grid w-grid-3">
            {[
              [
                'Bid your limit, not the price',
                'Enter the most you would pay. We bid the smallest amount needed to keep you in front and stop there — win under your maximum and you pay the lower number.',
              ],
              [
                'Late bids push the clock',
                extendMinutes === null
                  ? 'A bid placed as a lot is about to close moves the close out again, every time. Lots do not end on the dot, and nothing can be won by sniping the last second.'
                  : `A bid in the last ${String(extendMinutes)} minute${extendMinutes === 1 ? '' : 's'} moves the close out again, every time. Lots do not end on the dot, and nothing can be won by sniping the last second.`,
              ],
              [
                'A card on file is not a charge',
                auction?.requires_cc_preauth
                  ? `${houseName} verifies your card once, for a dollar, and releases it. You are billed after the sale, for the lots you actually won${premiumPct === null ? '' : `, plus the ${String(premiumPct)}% premium`}.`
                  : `${houseName} invoices you after the sale, for the lots you actually won${premiumPct === null ? '' : `, plus the ${String(premiumPct)}% premium`}. Nothing is charged while you bid.`,
              ],
            ].map(([heading, body], i) => (
              <div key={heading} style={{ paddingTop: 20, borderTop: '2px solid var(--text)' }}>
                <div className="w-lbl mono" style={{ marginBottom: 9 }}>
                  0{i + 1}
                </div>
                <h3 className="w-h3" style={{ fontSize: 18 }}>
                  {heading}
                </h3>
                <p className="w-body muted" style={{ marginTop: 8, fontSize: 14.5 }}>
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

/** One line in the hero card. White-on-brand, so it cannot use page tokens. */
function HeroRow({ label, value, tag }: { label: string; value: string; tag?: string }) {
  return (
    <div
      className="w-kv"
      style={{
        borderColor: 'color-mix(in oklab, var(--text-on-deep) 16%, transparent)',
        color: 'var(--text-on-deep)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {tag ? (
          <span className={`tag ${tag}`}>{label}</span>
        ) : (
          <span style={{ opacity: 0.72 }}>{label}</span>
        )}
      </span>
      <b className="mono">{value}</b>
    </div>
  );
}

/**
 * Which house? — the one routing job the old page did that is still needed.
 *
 * A bidder registered with several houses has no safe default: guessing drops
 * them into a catalogue they may not be able to bid in. One house auto-enters
 * (making somebody "choose" from a list of one is pure friction) and that
 * decision lives in `routeForHouses`, not here.
 */
function HousePicker({
  houses,
  onSelect,
}: {
  houses: readonly BidderHouse[];
  onSelect: (id: string) => void;
}) {
  return (
    <div className="w-wrap" style={{ maxWidth: 620, paddingTop: 56, paddingBottom: 56 }}>
      <h1 className="w-h1">Which auction house?</h1>
      <p className="w-body muted" style={{ marginTop: 12 }}>
        Your account is registered with more than one. Pick the one you want to enter — you can
        switch at any time from the header.
      </p>
      <div style={{ display: 'grid', gap: 12, marginTop: 28 }}>
        {houses.map((h) => {
          const logo =
            h.branding && typeof h.branding['logo_url'] === 'string'
              ? h.branding['logo_url']
              : null;
          return (
            <button
              key={h.tenant_id}
              type="button"
              onClick={() => onSelect(h.tenant_id)}
              className="w-card"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16 }}
            >
              {logo !== null && logo !== '' ? (
                <img src={logo} alt="" style={{ height: 38, width: 'auto', flexShrink: 0 }} />
              ) : (
                <span className="w-plate">{houseLabel(h).charAt(0).toUpperCase()}</span>
              )}
              <span className="w-h3" style={{ flex: 1, textAlign: 'left' }}>
                {houseLabel(h)}
              </span>
              <span aria-hidden className="muted">
                ›
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
